import React, { useRef, useState } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import WidgetCard from "../Widgets/WidgetCard";
import { handleViewRecordsClick, isViewRecordsAvailable } from "../utils/viewRecordsNavigation";
import CountWidget from "../Widgets/CountWidget";
import RateWidget from "../Widgets/RateWidget";
import CallOutcomesWidget from "../Widgets/CallOutcomesWidget";
import LinkedInOverviewWidget from "../Widgets/LinkedInOverviewWidget";
import CallsByMemberWidget from "../Widgets/CallsByMemberWidget";
import LatestRepliesWidget from "../Widgets/LatestRepliesWidget";
import CallsUtils from "Utils/CallsUtils";
import { WIDGET_META, RATE_WIDGET_IDS } from "../utils/widgetConstants";
import widgetDataStore from "../store/widgetDataStore";
import dashboardV2Store from "../store/dashboardV2Store";
import { toasterState } from "Components/common/toaster";
import { ReactComponent as PinIcon } from "Assets/svg/pin.svg";

const DATE_PRESET_LABELS = {
  today: "Today",
  yesterday: "Yesterday",
  lastWeek: "Last week",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  thisMonth: "This month"
};

const renderWidgetContent = (w, data, isLoading) => {
  const meta = WIDGET_META[w.widgetId];
  const isRate = RATE_WIDGET_IDS.has(w.widgetId);

  if (isRate) {
    return (
      <RateWidget
        currentRate={data?.currentRate}
        trend={data?.trend}
        chartData={data?.chartData || []}
        dateFilter={widgetDataStore.activeDateFilter}
      />
    );
  }
  if (w.widgetId === "CALL_OUTCOMES") {
    return <CallOutcomesWidget callOutcomes={data?.callOutcomes || []} />;
  }
  if (w.widgetId === "LINKEDIN_OVERVIEW") {
    return <LinkedInOverviewWidget data={data || {}} />;
  }
  if (w.widgetId === "CALLS_BY_MEMBER") {
    return <CallsByMemberWidget callsByMember={data?.callsByMember || []} />;
  }
  if (w.widgetId === "LATEST_REPLIES") {
    return <LatestRepliesWidget replies={data?.replies || []} isLoading={isLoading} />;
  }
  if (w.widgetId === "AVG_CALL_DURATION") {
    const seconds = data?.avgCallDuration;
    return (
      <CountWidget
        value={seconds != null ? CallsUtils.secondsToTime(seconds) : null}
        trend={data?.avgCallDurationTrend}
        series={null}
        dateFilter={widgetDataStore.activeDateFilter}
      />
    );
  }
  return (
    <CountWidget
      value={data?.[meta.valueKey]}
      trend={data?.[meta.trendKey]}
      series={data?.[meta.seriesKey]}
      dateFilter={widgetDataStore.activeDateFilter}
    />
  );
};

const WidgetGrid = observer(({ widgets }) => {
  const history = useHistory();
  const sortedWidgets = [...(widgets || [])].sort((a, b) => a.position - b.position);
  const pinnedWidgets = [...sortedWidgets].filter(w => w.pinned).sort((a, b) => a.pinnedOrder - b.pinnedOrder);
  const unpinnedWidgets = sortedWidgets.filter(w => !w.pinned);
  const subtitleLabel = DATE_PRESET_LABELS[widgetDataStore.activeDateFilter] || "";

  const dragSection = useRef(null);
  const dragIndex = useRef(null);
  const dragOverIndex = useRef(null);
  const dragAllowed = useRef(false);
  const [dragOverId, setDragOverId] = useState(null);

  if (!sortedWidgets.length) {
    return (
      <div className="widgetGrid">
        <div className="widgetGridEmptyState">
          <p>No widgets added yet.</p>
          <p>Click &ldquo;+ New widget&rdquo; to get started.</p>
        </div>
      </div>
    );
  }

  const handleMouseDown = e => {
    dragAllowed.current = !!e.target.closest(".widgetCardDragHandle");
  };

  const handleDragStart = (e, section, index) => {
    if (!dragAllowed.current) {
      e.preventDefault();
      return;
    }
    dragSection.current = section;
    dragIndex.current = index;
  };

  const handleDragEnter = (index, widgetId) => {
    dragOverIndex.current = index;
    setDragOverId(widgetId);
  };

  const handleDragEnd = (section, list, reorderFn) => {
    const from = dragIndex.current;
    const to = dragOverIndex.current;
    setDragOverId(null);
    dragIndex.current = null;
    dragOverIndex.current = null;
    dragSection.current = null;
    dragAllowed.current = false;
    if (from === null || to === null || from === to) return;

    const reordered = [...list];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    reorderFn(reordered);
  };

  const handleCrossSectionDragEnter = () => {
    if (dragSection.current !== null) {
      toasterState.setToastMsg("Pinned and unpinned widgets cannot be mixed", "fail");
    }
  };

  const renderDraggableCard = (w, index, section, list, reorderFn) => {
    const meta = WIDGET_META[w.widgetId];
    if (!meta) return null;

    const isRate = RATE_WIDGET_IDS.has(w.widgetId);
    const isListWidget = w.widgetId === "LATEST_REPLIES";
    const loadingKey = isRate || isListWidget ? w.widgetId : w.category;
    const isLoading = widgetDataStore.isCategoryLoading(loadingKey) || widgetDataStore.getWidgetData(w.widgetId) === null;
    const data = widgetDataStore.getWidgetData(w.widgetId);

    return (
      <div
        key={w.widgetId}
        draggable
        onMouseDown={handleMouseDown}
        onDragStart={e => handleDragStart(e, section, index)}
        onDragEnter={() => handleDragEnter(index, w.widgetId)}
        onDragEnd={() => handleDragEnd(section, list, reorderFn)}
        onDragOver={e => e.preventDefault()}
        onDrop={e => e.preventDefault()}
        className={`widgetCardDragWrap${dragOverId === w.widgetId ? " widgetCardDragOver" : ""}`}
      >
        <WidgetCard
          title={meta.label}
          subtitle={subtitleLabel}
          isLoading={isLoading}
          isPinned={w.pinned}
          onPin={() => dashboardV2Store.togglePin(w.widgetId)}
          onRemove={() => dashboardV2Store.removeWidget(w.widgetId)}
          onViewRecords={
            !isListWidget && isViewRecordsAvailable(w.widgetId)
              ? () => handleViewRecordsClick(w.widgetId, history)
              : null
          }
        >
          {renderWidgetContent(w, data, isLoading)}
        </WidgetCard>
      </div>
    );
  };

  return (
    <div className="widgetGridWrap">
      {pinnedWidgets.length > 0 && (
        <div
          className="widgetGridSection widgetGridPinnedSection"
          onDragEnter={() => dragSection.current === "unpinned" && handleCrossSectionDragEnter()}
        >
          <div className="widgetGridSectionHeader">
            <span className="widgetGridSectionLabel">
              <PinIcon className="widgetGridSectionPinIcon" />
              Pinned
            </span>
            <span className="widgetGridSectionCount">{pinnedWidgets.length} / 3</span>
          </div>
          <div className="widgetGrid">
            {pinnedWidgets.map((w, i) =>
              renderDraggableCard(w, i, "pinned", pinnedWidgets, dashboardV2Store.reorderPinnedWidgets)
            )}
          </div>
        </div>
      )}

      {unpinnedWidgets.length > 0 && (
        <div
          className="widgetGridSection"
          onDragEnter={() => dragSection.current === "pinned" && handleCrossSectionDragEnter()}
        >
          {pinnedWidgets.length > 0 && (
            <div className="widgetGridSectionHeader">
              <span className="widgetGridSectionLabel">All widgets</span>
            </div>
          )}
          <div className="widgetGrid widgetGridUnpinned">
            {unpinnedWidgets.map((w, i) =>
              renderDraggableCard(w, i, "unpinned", unpinnedWidgets, dashboardV2Store.reorderWidgets)
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export default WidgetGrid;
